/**
 * movimientos.js — UN SOLO lenguaje para los movimientos de dinero en el dashboard
 * Business (lo usan "Ultimos movimientos" del Inicio y la pagina Historial).
 *
 * Traduce lo tecnico a lo humano: nada de "ON RAMP", "RECHARGED_BY_USDC",
 * "Unkonw bank" ni IDs crudos. REGLA UX: el cliente NUNCA ve "USDC" — siempre USD.
 */

const norm = (t) => String(t || '').toUpperCase().replace(/[^A-Z]/g, '');

/** Etiqueta humana del movimiento + su descripcion util. */
export function describirMovimiento(t) {
  const raw = t.type || t.transaction_type || '';
  const k = norm(raw);
  const desc = String(t.description || '');
  const esCobro = !!t.cobroId || /cobro/i.test(desc);

  let label;
  if (k === 'COMISIONRECAUDO') label = 'Comisión de recaudo';
  else if (k.includes('OFFRAMP') || k.includes('WITHDRAW')) label = 'Retiro a banco';
  else if (k.includes('ONRAMP')) label = esCobro ? 'Cobro recibido' : 'Recarga por PSE';
  else if (k.includes('RECHARGEDBYUSDC') || k.includes('RECHARGE')) label = 'Recarga recibida';
  else if (k.includes('CONVERT')) label = 'Conversión entre pesos y dólares';
  // Mismo error que el signo: `amount` nunca viene negativo. Se usa la direccion real.
  else if (k.includes('TRANSFER')) label = direccionMovimiento(t) === 'sale' ? 'Transferencia enviada' : 'Transferencia recibida';
  else if (k.includes('REWARD')) label = 'Rendimientos';
  else label = raw ? raw.charAt(0) + raw.slice(1).toLowerCase().replace(/_/g, ' ') : 'Movimiento';

  // Detalle: preferimos la descripcion propia; si no, algo util (nunca "Unkonw bank")
  let detalle = '';
  if (desc && !/unkonw|unknown/i.test(desc)) detalle = desc;
  else if (esCobro) detalle = 'Pagado por tu cliente con PSE';
  else if (k.includes('ONRAMP')) detalle = 'Desde tu banco por PSE';
  else if (k.includes('RECHARGEDBYUSDC')) detalle = 'Entrada a tu cuenta';
  const banco = t.destination?.bankName || t.payvalida?.off_ramp?.bank || '';
  if (!detalle && banco && !/unkonw|unknown/i.test(banco)) detalle = banco;

  return { label, detalle };
}


/**
 * ?ENTRA O SALE? Se decide por el TIPO de movimiento, no por el signo del monto.
 *
 * El backend guarda `amount` SIEMPRE en positivo, asi que mirar `amount < 0`
 * hacia que todo — incluidos los retiros — se mostrara con "+" y en verde. El
 * dueno veia su nomina de 21 dolares como si le hubiera ENTRADO plata.
 *
 * Devuelve: 'sale' · 'entra' · 'neutro' (una conversion no cambia el patrimonio,
 * mueve entre bolsas: pintarla de rojo o verde seria mentir en ambos sentidos).
 */
export function direccionMovimiento(t) {
  const k = norm(t.type || t.transaction_type || '');
  const desc = String(t.description || '');

  if (k.includes('CONVERT')) return 'neutro';
  if (k.includes('OFFRAMP') || k.includes('WITHDRAW')) return 'sale';
  if (k.includes('ONRAMP') || k.includes('RECHARGE') || k.includes('REWARD')) return 'entra';

  // Transferencias: el texto dice para donde va mejor que cualquier bandera.
  if (/recib|received|entrada/i.test(desc)) return 'entra';
  if (/enviad|sent|externo|external/i.test(desc)) return 'sale';

  // Si el monto viniera firmado, se respeta.
  if (Number(t.amount) < 0) return 'sale';
  // Una transferencia del propio usuario, sin mas pistas, es una salida: es lo
  // que el hace desde su cuenta. Equivocarse hacia "entra" pinta de verde algo
  // que le vacio el saldo.
  if (k.includes('TRANSFER') || k.includes('INTERNAL')) return 'sale';
  return 'entra';
}

/** Monto formateado con su signo y moneda (USDC -> USD, siempre). */
export function montoMovimiento(t) {
  const cur = String(t.currency || 'USD').toUpperCase().replace('USDC', 'USD').replace('UNKNOWN', 'USD');
  const bruto = Number(t.amount || 0);
  // En un retiro el cliente ve el TOTAL que salio (monto + comisiones)
  const conFee = norm(t.type).includes('OFFRAMP') && t.fees ? bruto + Number(t.fees.total || 0) : bruto;
  const dir = direccionMovimiento(t);
  const signo = dir === 'sale' ? '−' : dir === 'entra' ? '+' : '';
  const abs = Math.abs(conFee);
  const valor = cur === 'COP'
    ? Math.round(abs).toLocaleString('es-CO')
    : abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return {
    texto: `${signo}${signo ? ' ' : ''}$${valor} ${cur}`,
    negativo: dir === 'sale',
    neutro: dir === 'neutro',
  };
}

/** Estado en español, con el color del chip. */
export function estadoMovimiento(t) {
  const s = String(t.status || '').toUpperCase();
  if (s === 'CANCELED' || s === 'CANCELLED') return { txt: 'Cancelado', cls: 'bg-gray-100 text-gray-500', dot: 'bg-gray-500' };
  if (s === 'FAILED' || s === 'REJECTED') return { txt: 'Rechazado', cls: 'bg-red-100 text-red-600', dot: 'bg-red-600' };
  if (s === 'CREATED' || s === 'PENDING') return { txt: 'En proceso', cls: 'bg-amber-100 text-amber-600', dot: 'bg-amber-600' };
  return { txt: 'Aprobado', cls: 'bg-green-100 text-green-600', dot: 'bg-green-600' };
}

/** Fecha y hora separadas (mismo formato del historial de personas). */
export function fechaHora(t) {
  const d = t.created_at || t.createdAt;
  if (!d) return { fecha: '—', hora: '' };
  const iso = new Date(d).toISOString();
  return { fecha: iso.slice(0, 10).replace(/-/g, '/'), hora: iso.slice(11, 19) };
}
